import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useEffect } from 'react';

import { Modal } from '@flipster/react/design-system/modal';
import { css } from '@flipster/styled-system/css';
import { vstack } from '@flipster/styled-system/patterns';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';
import { ProdStaticPath } from '@flipster/utils/url';

import Step from './Step';
import { OrderTutorialContext, OrderTutorialItem } from './constants';

interface Props extends OrderTutorialContext {
  step: number;
  onStepChange: (step: number) => void;
  items: OrderTutorialItem[];
  onFinish: () => void;
}

const OrderTutorialV2Content = ({ step, onStepChange, items, onFinish, ...onboardingContext }: Props) => {
  const t = useTranslations();
  useEffect(() => {
    segment.track(TradeEvent.TradingOnboardingPageView, {
      title: items[step].englishTitle,
      onboarding_page_num: step + 1,
    });
  }, [items, step]);

  return (
    <>
      <Modal.Header>
        <div className={vstack({ gap: 16, alignItems: 'normal' })}>
          <Step step={step} totalSteps={items.length} />
          <Modal.Title label={items[step].title} />
        </div>
      </Modal.Header>
      <Modal.Content
        css={css.raw({
          flex: 'initial',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          height: 366,
        })}
      >
        <Image
          src={`${ProdStaticPath}${items[step].imageUrl}`}
          width={480}
          height={200}
          alt={items[step].englishTitle}
        />
        {items[step].content(onboardingContext)}
      </Modal.Content>
      <Modal.Footer>
        {step > 0 && (
          <Modal.Button
            hierarchy='secondary'
            size='large'
            color='neutral'
            label={t('Autogen_common.r251126.s_Back')}
            onClick={() => {
              segment.track(TradeEvent.TradingOnboardingButtonClick, {
                title: items[step].englishTitle,
                onboarding_page_num: step + 1,
                button_text: 'Back',
              });
              onStepChange(step - 1);
            }}
          />
        )}
        {step < items.length - 1 ? (
          <Modal.Button
            hierarchy='primary'
            size='large'
            label={t('Autogen_common.r251126.s_Next')}
            onClick={() => {
              segment.track(TradeEvent.TradingOnboardingButtonClick, {
                title: items[step].englishTitle,
                onboarding_page_num: step + 1,
                button_text: 'Next',
              });
              onStepChange(step + 1);
            }}
          />
        ) : (
          <Modal.Button
            hierarchy='primary'
            size='large'
            label={t('Autogen_common.r251126.s_Start_now')}
            onClick={() => {
              segment.track(TradeEvent.TradingOnboardingButtonClick, {
                title: items[step].englishTitle,
                onboarding_page_num: step + 1,
                button_text: 'Start now',
              });
              onFinish();
            }}
          />
        )}
      </Modal.Footer>
    </>
  );
};

export default OrderTutorialV2Content;
